// AI-powered packing assistance service
// This provides intelligent suggestions based on trip parameters

export const packingAIService = {
  // Generate weather-based suggestions
  getWeatherSuggestions: (weather, destination) => {
    const suggestions = [];
    
    switch (weather) {
      case 'hot':
        suggestions.push(
          { name: 'Sunscreen SPF 30+', category: 'toiletries', priority: 'high' },
          { name: 'Light, breathable shirts', category: 'clothing', priority: 'high' },
          { name: 'Shorts', category: 'clothing', priority: 'high' },
          { name: 'Sun hat', category: 'accessories', priority: 'medium' },
          { name: 'Cooling towel', category: 'accessories', priority: 'low' }
        );
        break;
      case 'cold':
        suggestions.push(
          { name: 'Warm jacket/coat', category: 'clothing', priority: 'high' },
          { name: 'Gloves', category: 'accessories', priority: 'high' },
          { name: 'Thermal underwear', category: 'clothing', priority: 'medium' },
          { name: 'Warm hat', category: 'accessories', priority: 'medium' },
          { name: 'Hand warmers', category: 'accessories', priority: 'low' }
        );
        break;
      case 'rainy':
        suggestions.push(
          { name: 'Rain jacket', category: 'clothing', priority: 'high' },
          { name: 'Umbrella', category: 'accessories', priority: 'high' },
          { name: 'Waterproof shoes', category: 'shoes', priority: 'medium' },
          { name: 'Quick-dry clothes', category: 'clothing', priority: 'medium' }
        );
        break;
      default:
        suggestions.push(
          { name: 'Light jacket', category: 'clothing', priority: 'medium' },
          { name: 'Comfortable walking shoes', category: 'shoes', priority: 'high' }
        );
    }
    
    return suggestions;
  },

  // Generate trip type suggestions
  getTripTypeSuggestions: (tripType, duration) => {
    const suggestions = [];
    
    switch (tripType) {
      case 'business':
        suggestions.push(
          { name: 'Business cards', category: 'documents', priority: 'high' },
          { name: 'Laptop + charger', category: 'electronics', priority: 'high' },
          { name: 'Professional attire', category: 'clothing', priority: 'high' },
          { name: 'Dress shoes', category: 'shoes', priority: 'high' },
          { name: 'Portfolio/briefcase', category: 'accessories', priority: 'medium' }
        );
        break;
      case 'beach':
        suggestions.push(
          { name: 'Swimsuit', category: 'clothing', priority: 'high' },
          { name: 'Beach towel', category: 'accessories', priority: 'high' },
          { name: 'Flip flops', category: 'shoes', priority: 'high' },
          { name: 'Beach bag', category: 'accessories', priority: 'medium' },
          { name: 'Beach games', category: 'entertainment', priority: 'low' }
        );
        break;
      case 'camping':
        suggestions.push(
          { name: 'Tent', category: 'outdoor_gear', priority: 'high' },
          { name: 'Sleeping bag', category: 'outdoor_gear', priority: 'high' },
          { name: 'Flashlight', category: 'outdoor_gear', priority: 'high' },
          { name: 'First aid kit', category: 'medications', priority: 'high' },
          { name: 'Insect repellent', category: 'toiletries', priority: 'medium' }
        );
        break;
      default:
        suggestions.push(
          { name: 'Phone charger', category: 'electronics', priority: 'high' },
          { name: 'Comfortable clothes', category: 'clothing', priority: 'high' },
          { name: 'Personal medications', category: 'medications', priority: 'high' }
        );
    }
    
    return suggestions;
  },

  // Check for commonly forgotten items
  getCommonlyForgottenItems: () => {
    return [
      { name: 'Phone charger', category: 'electronics', priority: 'high' },
      { name: 'Toothbrush', category: 'toiletries', priority: 'high' },
      { name: 'Underwear', category: 'clothing', priority: 'high' },
      { name: 'Medications', category: 'medications', priority: 'high' },
      { name: 'Socks', category: 'clothing', priority: 'high' },
      { name: 'Pajamas', category: 'clothing', priority: 'medium' },
      { name: 'Deodorant', category: 'toiletries', priority: 'medium' },
      { name: 'Hair brush', category: 'toiletries', priority: 'medium' },
      { name: 'Contact solution', category: 'toiletries', priority: 'low' },
      { name: 'Adapter/converter', category: 'electronics', priority: 'low' }
    ];
  },

  // Activity-based suggestions
  getActivitySuggestions: (activities = []) => {
    const suggestions = [];
    
    if (activities.includes('swimming')) {
      suggestions.push(
        { name: 'Swimsuit', category: 'clothing', priority: 'high' },
        { name: 'Swim goggles', category: 'accessories', priority: 'medium' },
        { name: 'Pool towel', category: 'accessories', priority: 'high' }
      );
    }
    
    if (activities.includes('hiking')) {
      suggestions.push(
        { name: 'Hiking boots', category: 'shoes', priority: 'high' },
        { name: 'Backpack', category: 'accessories', priority: 'high' },
        { name: 'Water bottle', category: 'outdoor_gear', priority: 'high' },
        { name: 'Trail snacks', category: 'other', priority: 'medium' }
      );
    }
    
    if (activities.includes('dining')) {
      suggestions.push(
        { name: 'Nice dinner outfit', category: 'clothing', priority: 'medium' },
        { name: 'Dress shoes', category: 'shoes', priority: 'medium' }
      );
    }
    
    return suggestions;
  },

  // Comprehensive AI analysis
  getAISuggestions: (tripData) => {
    const { tripType, weather, destination, duration, activities = [] } = tripData;
    
    const allSuggestions = [
      ...packingAIService.getWeatherSuggestions(weather, destination),
      ...packingAIService.getTripTypeSuggestions(tripType, duration),
      ...packingAIService.getActivitySuggestions(activities),
      ...packingAIService.getCommonlyForgottenItems()
    ];
    
    // Remove duplicates and sort by priority
    const uniqueSuggestions = allSuggestions.reduce((acc, item) => {
      const exists = acc.find(existing => existing.name.toLowerCase() === item.name.toLowerCase());
      if (!exists) {
        acc.push(item);
      }
      return acc;
    }, []);
    
    // Sort by priority (high, medium, low)
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    uniqueSuggestions.sort((a, b) => priorityOrder[b.priority] - priorityOrder[a.priority]);
    
    return uniqueSuggestions.slice(0, 15); // Limit to top 15 suggestions
  },

  // Smart quantity suggestions based on trip duration
  suggestQuantity: (itemName, duration) => {
    const lowerName = itemName.toLowerCase();
    
    if (lowerName.includes('shirt') || lowerName.includes('top')) {
      return Math.max(2, Math.ceil(duration * 0.8));
    }
    if (lowerName.includes('underwear')) {
      return duration + 1; // One extra
    }
    if (lowerName.includes('sock')) {
      return Math.ceil(duration * 1.2);
    }
    if (lowerName.includes('pants') || lowerName.includes('bottom')) {
      return Math.max(2, Math.ceil(duration * 0.5));
    }
    
    return 1; // Default quantity
  }
};

export default packingAIService;